import Image from "next/image";
import Link from "next/link";
import { ReactNode, ComponentType, useEffect, useState } from "react";

interface CTAButton {
	text: string;
	href: string;
	icon?: ComponentType<{ className?: string }>;
	variant?: "primary" | "secondary";
}

interface HomepageHeroProps {
	backgroundImage: string;
	headline: ReactNode;
	subheadline: ReactNode;
	bracketedText?: string;
	bracketAnimateColors?: boolean;
	ctaButtons?: CTAButton[];
}

export default function HomepageHero({
	backgroundImage,
	headline,
	subheadline,
	bracketedText,
	bracketAnimateColors = false,
	ctaButtons,
}: HomepageHeroProps) {
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		// Trigger animation on mount
		setIsVisible(true);
	}, []);

	const renderHeadlineWithBrackets = () => {
		if (!bracketedText) {
			if (typeof headline === "string") {
				return <span dangerouslySetInnerHTML={{ __html: headline }} />;
			}
			return <>{headline}</>;
		}

		if (typeof headline === "string") {
			const headlineStr = headline;
			const index = headlineStr.indexOf(bracketedText);

			if (index === -1) {
				return <span dangerouslySetInnerHTML={{ __html: headline }} />;
			}

			const before = headlineStr.substring(0, index);
			const after = headlineStr.substring(index + bracketedText.length);

			return (
				<>
					{before && <span className="text-white" dangerouslySetInnerHTML={{ __html: before }} />}
					<span className="relative inline-block whitespace-normal md:whitespace-nowrap break-words">
						<span 
							className="relative z-10"
							style={{
								background: 'linear-gradient(135deg, #ffffff 0%, #9DADFC 40%, #6C84FA 100%)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
								backgroundClip: 'text',
							}}
							dangerouslySetInnerHTML={{ __html: bracketedText }} 
						/>
						{/* Subtle gradient underline */}
						<span 
							className="absolute bottom-0 left-0 right-0 h-[2px] md:h-[3px] rounded-full"
							style={{
								background: 'linear-gradient(90deg, transparent 0%, rgba(108, 132, 250, 0.4) 50%, transparent 100%)',
								opacity: 0.6,
							}}
						/>
						{/* Subtle glow effect */}
						<span 
							className="absolute bottom-0 left-0 right-0 h-[4px] md:h-[6px] rounded-full opacity-20 blur-sm"
							style={{
								background: 'linear-gradient(90deg, transparent 0%, rgba(108, 132, 250, 0.3) 50%, transparent 100%)',
							}}
						/>
					</span>
					{after && <span className="text-white" dangerouslySetInnerHTML={{ __html: after }} />}
				</>
			);
		}

		return <>{headline}</>;
	};

	return (
		<div className="relative w-full min-h-[85vh] md:min-h-screen bg-slate-900 flex items-center overflow-hidden">
			{/* Background */}
			<Image
				src={backgroundImage}
				alt="Background"
				layout="fill"
				objectFit="cover"
				className="z-0"
				priority
			/>
			<div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-black/70 z-0" />


			{/* Content */}
			<div className="relative z-10 container mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-28 md:pb-24 max-w-5xl">
				{/* Headline */}
				<h1 
					className={`text-[clamp(28px,5vw,64px)] leading-[1.15] font-display font-black mb-6 md:mb-8 tracking-tight break-words transition-all duration-1000 ease-out ${
						isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
					}`}
					style={{
						filter: 'drop-shadow(0 2px 8px rgba(108, 132, 250, 0.1))',
					}}
				>
					{renderHeadlineWithBrackets()}
				</h1>

				{/* Subheadline */}
				<p 
					className={`text-[clamp(16px,1.5vw,22px)] leading-relaxed text-white/80 max-w-2xl mb-8 md:mb-12 transition-all duration-1000 ease-out delay-300 ${
						isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
					}`}
				>
					{subheadline}
				</p>

				{/* CTA Buttons */}
				{ctaButtons && ctaButtons.length > 0 && (
					<div className={`flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4 transition-all duration-1000 ease-out delay-500 ${
						isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
					}`}>
						{ctaButtons.map((cta, index) => {
							const ButtonIcon = cta.icon;
							const isPrimary = cta.variant !== "secondary";
							return (
								<Link key={index} href={cta.href}>
									<a
										className={`font-display font-semibold py-3 md:py-4 px-6 md:px-8 rounded-lg transition-all text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 ${
											isPrimary
												? "bg-blue text-white hover:bg-blue-dark hover:scale-[1.02] shadow-lg shadow-blue/25"
												: "bg-white/10 text-white border border-white/20 hover:bg-white/20 hover:border-white/40 backdrop-blur-sm"
										}`}
									>
										{ButtonIcon && <ButtonIcon className="w-5 h-5 flex-shrink-0" />}
										<span>{cta.text}</span>
									</a>
								</Link>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
